import { useMemo, useState } from "react";
import { SSP, TAX_YEAR, gbp } from "@/lib/statutory";

export function SSPCalculator() {
  const [sickDays, setSickDays] = useState(10);
  const [qualifyingDaysPerWeek, setQualifyingDaysPerWeek] = useState(5);
  const [earnsAboveLEL, setEarnsAboveLEL] = useState(true);
  const [waitingDaysServed, setWaitingDaysServed] = useState(0);

  const result = useMemo(() => {
    if (!earnsAboveLEL) {
      return { eligible: false, ssp: 0, payableDays: 0, waitingDays: 0 };
    }
    const waitingNeeded = Math.max(0, SSP.waitingDays - waitingDaysServed);
    const payableDays = Math.max(0, sickDays - waitingNeeded);
    const dailyRate = SSP.weeklyRate / Math.max(1, qualifyingDaysPerWeek);
    const maxPayable = qualifyingDaysPerWeek * SSP.maxWeeks;
    const ssp = Math.min(payableDays, maxPayable) * dailyRate;
    return { eligible: true, ssp, payableDays, waitingDays: waitingNeeded };
  }, [sickDays, qualifyingDaysPerWeek, earnsAboveLEL, waitingDaysServed]);

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <NumberField label="Days of sickness absence" value={sickDays} onChange={setSickDays} min={0} max={140} />
        <NumberField
          label="Qualifying days per week"
          value={qualifyingDaysPerWeek}
          onChange={setQualifyingDaysPerWeek}
          min={1}
          max={7}
        />
        <NumberField
          label="Waiting days already served"
          value={waitingDaysServed}
          onChange={setWaitingDaysServed}
          min={0}
          max={SSP.waitingDays}
        />
        <label className="flex items-end gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={earnsAboveLEL}
            onChange={(e) => setEarnsAboveLEL(e.target.checked)}
            className="size-4 accent-[color:var(--accent)]"
          />
          Employee earns at or above the Lower Earnings Limit
        </label>
      </div>

      <div className="mt-8 rounded-2xl bg-brand p-6 text-brand-foreground">
        {result.eligible ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">
              Statutory Sick Pay due
            </p>
            <p className="mt-3 font-serif text-5xl">{gbp(result.ssp, { decimals: 2 })}</p>
            <p className="mt-3 text-sm text-brand-foreground/70">
              {result.payableDays} payable days at {gbp(SSP.weeklyRate)}/week ({TAX_YEAR}). {result.waitingDays} waiting
              day(s) unpaid.
            </p>
          </>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">Not eligible</p>
            <p className="mt-3 font-serif text-3xl">£0.00</p>
            <p className="mt-3 text-sm text-brand-foreground/70">
              Employees earning below the Lower Earnings Limit ({gbp(SSP.lowerEarningsLimit)}/week, {TAX_YEAR}) are not
              entitled to SSP.
            </p>
          </>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            SSP is payable at {gbp(SSP.weeklyRate)} per week ({TAX_YEAR} rate) for up to {SSP.maxWeeks} weeks. The first{" "}
            {SSP.waitingDays} qualifying days are unpaid "waiting days" unless the employee was paid SSP within the last
            8 weeks.
          </p>
          <p>
            Qualifying days are the days the employee normally works. The daily rate is the weekly rate divided by the
            number of qualifying days in the week.
          </p>
        </div>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
